import { useCallback, useEffect, useMemo, useState } from "react";
import { Container, Box, Typography, Stack, Grid, Paper, Chip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Icon } from "@iconify/react";
import PromoBannerSlider from "../components/PromoBannerSlider";
import TripSearchForm from "../components/TripSearchForm";

import Footer from "../components/Footer";
import SearchOverlay from "../components/SearchOverlay";
import {
    setLoading,
    setTripResults,
    setError,
    setDepartureStation,
    setArrivalStation,
    setJourneyDate,
} from "../redux/slices/searchSlice";
import { searchTripsApi } from "../api/trips";
import { getActivePromoBannersApi } from "../api/promoBanners";
import { ANDROID_APP_URL, IOS_APP_URL } from "../config/appStores";

const HERO_IMAGE = "/assets/banners/hero.jpg";

const BANNER_IMAGES = {
    carriers: "/assets/banners/prevoznici.jpg",
    openReturn: "/assets/banners/povratak.jpg",
    group: "/assets/banners/putnici.jpg",
};

// Promo kartice mobilne aplikacije — optimizovane kopije (originali 1242×2688 po
// ~1–3 MB stoje u istom folderu); tamna familija se poklapa sa brend tegetom.
const APP_SHOTS = [
    { src: "/assets/mobile-screens/promo-pretraga.png", altKey: "homePage.appShot1Alt" },
    { src: "/assets/mobile-screens/promo-karta.png", altKey: "homePage.appShot2Alt" },
    { src: "/assets/mobile-screens/promo-placanje.png", altKey: "homePage.appShot3Alt" },
];

const formatPromoPrice = (price) => {
    const value = Number(price);
    const digits = Number.isInteger(value) ? 0 : 2;
    return `${value.toLocaleString("sr-Latn", { minimumFractionDigits: digits, maximumFractionDigits: digits })} RSD`;
};

export default function HomePage() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [searching, setSearching] = useState(false);
    const [promoBanners, setPromoBanners] = useState([]);

    useEffect(() => {
        let alive = true;
        getActivePromoBannersApi()
            .then((list) => {
                if (alive) setPromoBanners(list);
            })
            .catch(() => {});
        return () => {
            alive = false;
        };
    }, []);

    const scrollToSearch = useCallback(() => window.scrollTo({ top: 0, behavior: "smooth" }), []);

    const handleSearch = useCallback(
        async (searchParams) => {
            if (searching) return;
            setSearching(true);
            dispatch(setLoading(true));
            dispatch(setError(null));
            try {
                const results = await searchTripsApi(searchParams);
                dispatch(setTripResults(results || []));
                navigate("/rezultati");
            } catch (error) {
                console.error("Greška pri pretrazi:", error);
                dispatch(setError(t("homePage.searchError")));
                dispatch(setTripResults([]));
                navigate("/rezultati");
            } finally {
                dispatch(setLoading(false));
                setSearching(false);
            }
        },
        [dispatch, navigate, searching, t]
    );

    const openPromoRoute = useCallback(
        (banner) => {
            const departure = { id: banner.fromStationId, name: banner.fromStationName, stationName: banner.fromStationName };
            const arrival = { id: banner.toStationId, name: banner.toStationName, stationName: banner.toStationName };
            dispatch(setDepartureStation(departure));
            dispatch(setArrivalStation(arrival));
            dispatch(setJourneyDate(banner.nextDate));
            handleSearch({ departureStationId: departure.id, arrivalStationId: arrival.id, from: banner.nextDate });
        },
        [dispatch, handleSearch]
    );

    const sliderBanners = useMemo(() => {
        if (promoBanners.length > 0) {
            return promoBanners.map((banner) => {
                const price = formatPromoPrice(banner.minPrice);
                return {
                    key: `promo-${banner.id}`,
                    src: banner.imageUrl,
                    alt: banner.title,
                    label: `${banner.title}, ${banner.fromStationName} - ${banner.toStationName}, ${t(
                        "homePage.promoPriceFrom"
                    )} ${price}`,
                    onClick: () => openPromoRoute(banner),
                    content: {
                        carrier: banner.carrierName,
                        title: banner.title,
                        from: banner.fromStationName,
                        to: banner.toStationName,
                        subtitle: banner.subtitle,
                        body: banner.body,
                        priceFrom: t("homePage.promoPriceFrom"),
                        price,
                        cta: t("homePage.promoCta"),
                    },
                };
            });
        }
        return [
            {
                src: BANNER_IMAGES.carriers,
                alt: t("homePage.banner1Alt"),
                label: t("homePage.banner1Title"),
                onClick: scrollToSearch,
            },
            {
                src: BANNER_IMAGES.openReturn,
                alt: t("homePage.banner2Alt"),
                label: t("homePage.banner2Title"),
                onClick: () => navigate("/pomoc"),
            },
            {
                src: BANNER_IMAGES.group,
                alt: t("homePage.banner3Alt"),
                label: t("homePage.banner3Title"),
                onClick: scrollToSearch,
            },
        ];
    }, [promoBanners, openPromoRoute, navigate, scrollToSearch, t]);

    return (
        <Box>
            <Helmet>
                <title>{t("homePage.seoTitle")}</title>
                <meta name="description" content={t("homePage.seoDescription")} />
                <meta property="og:title" content={t("homePage.seoTitle")} />
                <meta property="og:description" content={t("homePage.seoDescription")} />
                <meta property="og:type" content="website" />
                <meta property="og:image" content="https://busgo.travel/assets/logo/appLogo.png" />
            </Helmet>
            <SearchOverlay open={searching} />
            {/* ===== HERO SECTION ===== */}
            <Box
                sx={{
                    position: "relative",
                    minHeight: { xs: "auto", md: 700 },
                    display: "flex",
                    alignItems: "center",
                    pt: { xs: 12, md: 0 },
                    pb: { xs: 6, md: 0 },
                }}
            >
                {/* Background Image */}
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundImage: `url(${HERO_IMAGE})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: "linear-gradient(135deg, rgba(26,26,46,0.9) 0%, rgba(0,0,0,0.6) 50%, rgba(22,33,62,0.85) 100%)",
                        },
                    }}
                />

                <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
                    <Stack alignItems="center" sx={{ mb: { xs: 3, md: 5 } }}>
                        <Chip
                            icon={<Icon icon="mdi:shield-check" width={16} />}
                            label={t("homePage.heroBadge")}
                            sx={{
                                mb: 3,
                                bgcolor: "rgba(255,255,255,0.15)",
                                color: "white",
                                fontWeight: 700,
                                letterSpacing: "2px",
                                fontSize: "0.7rem",
                                backdropFilter: "blur(4px)",
                                border: "1px solid rgba(255,255,255,0.2)",
                                "& .MuiChip-icon": { color: "#84A9FF" },
                            }}
                        />
                        <Typography
                            variant="h2"
                            component="h1"
                            sx={{
                                fontWeight: 900,
                                textAlign: "center",
                                color: "white",
                                fontSize: { xs: "2rem", md: "3.5rem" },
                                lineHeight: 1.15,
                                maxWidth: 700,
                                textShadow: "0 2px 20px rgba(0,0,0,0.3)",
                            }}
                        >
                            {t("homePage.heroTitleLine1")}
                            <br />
                            <Box component="span" sx={{ color: "#84A9FF" }}>
                                {t("homePage.heroTitleLine2")}
                            </Box>
                        </Typography>
                        <Typography
                            variant="h6"
                            component="p"
                            sx={{
                                textAlign: "center",
                                mt: 2,
                                maxWidth: 520,
                                color: "rgba(255,255,255,0.8)",
                                fontWeight: 400,
                                lineHeight: 1.6,
                            }}
                        >
                            {t("homePage.heroSubtitle")}
                        </Typography>
                    </Stack>

                    <TripSearchForm onSearch={handleSearch} variant="hero" isLoading={searching} />
                </Container>

                {/* Wave Bottom */}
                <Box
                    sx={{
                        position: "absolute",
                        bottom: -1,
                        left: 0,
                        right: 0,
                        height: 80,
                        overflow: "hidden",
                    }}
                >
                    <svg viewBox="0 0 1440 80" preserveAspectRatio="none" style={{ width: "100%", height: "100%" }}>
                        <path
                            d="M0,40 C360,80 720,0 1080,40 C1260,60 1380,50 1440,40 L1440,80 L0,80 Z"
                            fill="white"
                        />
                    </svg>
                </Box>
            </Box>

            {/* ===== STATS BAR ===== */}
            <Box sx={{ bgcolor: "white", py: 4, mt: -1 }}>
                <Container maxWidth="lg">
                    <Grid container spacing={3} justifyContent="center">
                        {/* TODO: brojke 500+/200+ (i „50.000 putnika" u whyDesc prevodima) su
                            placeholder — povući stvarne vrednosti iz baze (broj polazaka,
                            destinacija, kupovina) kad prodaja krene. */}
                        {[
                            { icon: "mdi:bus-multiple", value: "500+", label: t("homePage.statsDepartures") },
                            { icon: "mdi:map-marker-multiple", value: "200+", label: t("homePage.statsDestinations") },
                            { icon: "mdi:shield-check", value: "100%", label: t("homePage.statsSecure") },
                            { icon: "mdi:headset", value: "00–24h", label: t("homePage.statsSupport") },
                        ].map((stat) => (
                            <Grid item xs={6} md={3} key={stat.label}>
                                <Stack alignItems="center" spacing={0.5}>
                                    <Box
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: 3,
                                            bgcolor: "primary.lighter",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            mb: 1,
                                        }}
                                    >
                                        <Icon icon={stat.icon} width={28} color="#1a1a2e" />
                                    </Box>
                                    <Typography variant="h4" component="div" sx={{ fontWeight: 900, color: "primary.dark" }}>
                                        {stat.value}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        {stat.label}
                                    </Typography>
                                </Stack>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* ===== PREUZMITE APLIKACIJU ===== */}
            <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: "white" }}>
                <Container maxWidth="lg">
                    <Stack alignItems="center" sx={{ mb: 5 }}>
                        <Chip
                            label={t("homePage.appBadge")}
                            sx={{
                                mb: 2,
                                fontWeight: 700,
                                letterSpacing: "2px",
                                bgcolor: "primary.lighter",
                                color: "primary.dark",
                                fontSize: "0.75rem",
                            }}
                        />
                        <Typography variant="h3" component="h2" sx={{ fontWeight: 800, textAlign: "center", mb: 1 }}>
                            {t("homePage.appTitle")}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center", maxWidth: 500 }}>
                            {t("homePage.appSubtitle")}
                        </Typography>
                    </Stack>

                    <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
                        <Grid item xs={12} md={5}>
                            <Stack spacing={3}>
                                {[
                                    { icon: "mdi:ticket-confirmation-outline", title: t("homePage.appFeature1Title"), desc: t("homePage.appFeature1Desc") },
                                    { icon: "mdi:bell-ring-outline", title: t("homePage.appFeature2Title"), desc: t("homePage.appFeature2Desc") },
                                    { icon: "mdi:qrcode-scan", title: t("homePage.appFeature3Title"), desc: t("homePage.appFeature3Desc") },
                                ].map((item) => (
                                    <Stack key={item.title} direction="row" spacing={2} alignItems="flex-start">
                                        <Box
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                borderRadius: 2,
                                                bgcolor: "primary.lighter",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexShrink: 0,
                                            }}
                                        >
                                            <Icon icon={item.icon} width={24} color="#1a1a2e" />
                                        </Box>
                                        <Stack>
                                            <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700 }}>
                                                {item.title}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {item.desc}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                ))}
                            </Stack>

                            <Stack
                                direction="row"
                                spacing={2}
                                justifyContent={{ xs: "center", md: "flex-start" }}
                                sx={{ mt: 4 }}
                            >
                                {/* Bedž je link kad je aplikacija na prodavnici, inače najava
                                    („Uskoro"). Adrese su u config/appStores.js. */}
                                {[
                                    { icon: "mdi:google-play", label: "Google Play", href: ANDROID_APP_URL },
                                    { icon: "mdi:apple", label: "App Store", href: IOS_APP_URL },
                                ].map((store) => (
                                    <Box
                                        key={store.label}
                                        component={store.href ? "a" : "div"}
                                        href={store.href || undefined}
                                        target={store.href ? "_blank" : undefined}
                                        rel={store.href ? "noopener noreferrer" : undefined}
                                        aria-disabled={store.href ? undefined : "true"}
                                        aria-label={store.href ? `${t("homePage.downloadOn")} ${store.label}` : undefined}
                                        sx={{
                                            position: "relative",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1.5,
                                            px: 3,
                                            py: 1.5,
                                            borderRadius: 3,
                                            bgcolor: "#1a1a2e",
                                            color: "white",
                                            textDecoration: "none",
                                            opacity: store.href ? 1 : 0.55,
                                            cursor: store.href ? "pointer" : "default",
                                            transition: "transform 0.2s, box-shadow 0.2s",
                                            ...(store.href && {
                                                "&:hover": {
                                                    transform: "translateY(-2px)",
                                                    boxShadow: "0 8px 20px rgba(26,26,46,0.35)",
                                                },
                                            }),
                                        }}
                                    >
                                        <Icon icon={store.icon} width={32} />
                                        <Stack>
                                            <Typography sx={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.7)", lineHeight: 1 }}>
                                                {t("homePage.downloadOn")}
                                            </Typography>
                                            <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", lineHeight: 1.2 }}>
                                                {store.label}
                                            </Typography>
                                        </Stack>
                                        {!store.href && (
                                            <Chip
                                                label={t("homePage.comingSoon")}
                                                size="small"
                                                sx={{
                                                    position: "absolute",
                                                    top: -10,
                                                    right: -8,
                                                    height: 20,
                                                    fontSize: "0.6rem",
                                                    fontWeight: 700,
                                                    bgcolor: "#84A9FF",
                                                    color: "#1a1a2e",
                                                }}
                                            />
                                        )}
                                    </Box>
                                ))}
                            </Stack>
                        </Grid>

                        <Grid item xs={12} md={7}>
                            {/* Lepeza od tri promo kartice (md+): srednja u fokusu, bočne
                                blago rotirane iza i posvetljene velom (skida se na hover);
                                sve tri lagano lebde u različitom ritmu. Kartice su gotovi
                                vizuali sa naslovom, pa nema HTML teksta preko njih. */}
                            <Box
                                sx={{
                                    display: { xs: "none", md: "flex" },
                                    justifyContent: "center",
                                    alignItems: "flex-end",
                                    py: 3,
                                    "@keyframes cardFloat": {
                                        "0%, 100%": { transform: "translateY(0)" },
                                        "50%": { transform: "translateY(-9px)" },
                                    },
                                }}
                            >
                                {APP_SHOTS.map((shot, i) => {
                                    const center = i === 1;
                                    return (
                                        <Box
                                            key={shot.src}
                                            sx={{
                                                zIndex: center ? 2 : 1,
                                                mx: center ? -3 : 0,
                                                transform: center
                                                    ? "none"
                                                    : i === 0
                                                        ? "rotate(-7deg) translateY(14px)"
                                                        : "rotate(7deg) translateY(14px)",
                                                transition: "transform .3s ease",
                                                "&:hover": {
                                                    transform: center
                                                        ? "translateY(-10px)"
                                                        : `rotate(${i === 0 ? -4 : 4}deg) translateY(2px)`,
                                                    zIndex: 3,
                                                },
                                                "&:hover .app-shot-veil": { opacity: 0 },
                                                "&:hover .app-shot-card": {
                                                    boxShadow: "0 28px 56px rgba(26,26,46,0.32)",
                                                },
                                            }}
                                        >
                                            <Box
                                                className="app-shot-card"
                                                sx={{
                                                    position: "relative",
                                                    width: center ? 250 : 205,
                                                    borderRadius: 5,
                                                    overflow: "hidden",
                                                    boxShadow: center
                                                        ? "0 24px 48px rgba(26,26,46,0.28)"
                                                        : "0 12px 28px rgba(26,26,46,0.16)",
                                                    transition: "box-shadow .3s ease",
                                                    // lebdenje: razmaknuti ritam i faza po kartici
                                                    "@media (prefers-reduced-motion: no-preference)": {
                                                        animation: `cardFloat ${5.5 + i * 0.9}s ease-in-out ${i * 0.8}s infinite`,
                                                    },
                                                    // svetliji veo preko bočnih — srednja ostaje puna
                                                    "& .app-shot-veil": {
                                                        position: "absolute",
                                                        inset: 0,
                                                        bgcolor: "rgba(255,255,255,0.22)",
                                                        opacity: center ? 0 : 1,
                                                        transition: "opacity .3s ease",
                                                        pointerEvents: "none",
                                                    },
                                                }}
                                            >
                                                <Box
                                                    component="img"
                                                    src={shot.src}
                                                    alt={t(shot.altKey)}
                                                    loading="lazy"
                                                    sx={{ display: "block", width: "100%" }}
                                                />
                                                <Box className="app-shot-veil" />
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>

                            {/* Na telefonu: horizontalna swipe traka istih kartica. */}
                            <Box
                                sx={{
                                    display: { xs: "flex", md: "none" },
                                    gap: 2,
                                    overflowX: "auto",
                                    scrollSnapType: "x mandatory",
                                    scrollbarWidth: "none",
                                    "&::-webkit-scrollbar": { display: "none" },
                                    px: 1,
                                    py: 2,
                                }}
                            >
                                {APP_SHOTS.map((shot) => (
                                    <Box
                                        key={shot.src}
                                        component="img"
                                        src={shot.src}
                                        alt={t(shot.altKey)}
                                        loading="lazy"
                                        sx={{
                                            width: "62%",
                                            flex: "0 0 auto",
                                            scrollSnapAlign: "center",
                                            borderRadius: 4,
                                            boxShadow: "0 12px 28px rgba(26,26,46,0.18)",
                                        }}
                                    />
                                ))}
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* ===== HOW IT WORKS ===== */}
            <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: "grey.50" }}>
                <Container maxWidth="lg">
                    <Stack alignItems="center" sx={{ mb: 6 }}>
                        <Chip
                            label={t("homePage.howBadge")}
                            sx={{
                                mb: 2,
                                fontWeight: 700,
                                letterSpacing: "2px",
                                bgcolor: "secondary.lighter",
                                color: "secondary.dark",
                                fontSize: "0.75rem",
                            }}
                        />
                        <Typography variant="h3" component="h2" sx={{ fontWeight: 800, textAlign: "center", mb: 1 }}>
                            {t("homePage.howTitle")}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center", maxWidth: 480 }}>
                            {t("homePage.howSubtitle")}
                        </Typography>
                    </Stack>

                    <Grid container spacing={4}>
                        {[
                            {
                                step: "01",
                                icon: "mdi:map-search",
                                title: t("homePage.step1Title"),
                                description: t("homePage.step1Desc"),
                                color: "#1a1a2e",
                            },
                            {
                                step: "02",
                                icon: "mdi:seat-recline-normal",
                                title: t("homePage.step2Title"),
                                description: t("homePage.step2Desc"),
                                color: "#3366FF",
                            },
                            {
                                step: "03",
                                icon: "mdi:ticket-confirmation-outline",
                                title: t("homePage.step3Title"),
                                description: t("homePage.step3Desc"),
                                color: "#FF5630",
                            },
                        ].map((item, index) => (
                            <Grid item xs={12} md={4} key={item.step}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 4,
                                        borderRadius: 4,
                                        textAlign: "center",
                                        bgcolor: "white",
                                        border: "1px solid",
                                        borderColor: "grey.200",
                                        position: "relative",
                                        overflow: "hidden",
                                        transition: "all 0.3s",
                                        "&:hover": {
                                            borderColor: item.color,
                                            boxShadow: `0 8px 40px ${item.color}20`,
                                            transform: "translateY(-4px)",
                                        },
                                    }}
                                >
                                    {/* Redni broj je vodeni žig, a ne sadržaj: ide kao CSS dekor
                                        da ga čitač ekrana ne izgovara i da se ne meri kao tekst
                                        (namerno je jedva vidljiv, pa bi pao proveru kontrasta). */}
                                    <Box
                                        aria-hidden="true"
                                        sx={{
                                            position: "absolute",
                                            top: 12,
                                            right: 16,
                                            fontSize: "3.5rem",
                                            fontWeight: 900,
                                            color: "grey.100",
                                            lineHeight: 1,
                                            pointerEvents: "none",
                                            "&::before": { content: `"${item.step}"` },
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            width: 72,
                                            height: 72,
                                            borderRadius: "50%",
                                            bgcolor: `${item.color}15`,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            mx: "auto",
                                            mb: 2.5,
                                        }}
                                    >
                                        <Icon icon={item.icon} width={36} color={item.color} />
                                    </Box>
                                    <Typography variant="h6" component="h3" sx={{ fontWeight: 700, mb: 1 }}>
                                        {item.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                                        {item.description}
                                    </Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* ===== PROMO BANNERS ===== */}
            <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: "white" }}>
                <Container maxWidth="lg">
                    {/* Klizač umesto mreže: sve pločice su istog odnosa i veličine,
                        pa nijedna slika nije povlašćena, a na dodir radi swipe. */}
                    <PromoBannerSlider banners={sliderBanners} />
                </Container>
            </Box>

            {/* ===== TESTIMONIALS / TRUST ===== */}
            <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: "grey.50" }}>
                <Container maxWidth="lg">
                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={5}>
                            <Chip
                                label={t("homePage.whyBadge")}
                                sx={{
                                    mb: 2,
                                    fontWeight: 700,
                                    letterSpacing: "2px",
                                    bgcolor: "warning.lighter",
                                    // tamnija nijansa jer je warning.dark na ovoj podlozi
                                    // ispod praga čitljivosti (3,7 : 1)
                                    color: "warning.darker",
                                    fontSize: "0.75rem",
                                }}
                            />
                            <Typography variant="h3" component="h2" sx={{ fontWeight: 800, mb: 2 }}>
                                {t("homePage.whyTitle")}
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.8 }}>
                                {t("homePage.whyDesc")}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <Grid container spacing={2}>
                                {[
                                    { icon: "mdi:shield-lock", title: t("homePage.trust1Title"), desc: t("homePage.trust1Desc") },
                                    { icon: "mdi:clock-fast", title: t("homePage.trust2Title"), desc: t("homePage.trust2Desc") },
                                    { icon: "mdi:headset", title: t("homePage.trust3Title"), desc: t("homePage.trust3Desc") },
                                    { icon: "mdi:cash-refund", title: t("homePage.trust4Title"), desc: t("homePage.trust4Desc") },
                                    { icon: "mdi:cellphone-check", title: t("homePage.trust5Title"), desc: t("homePage.trust5Desc") },
                                    { icon: "mdi:percent", title: t("homePage.trust6Title"), desc: t("homePage.trust6Desc") },
                                ].map((item) => (
                                    <Grid item xs={6} key={item.title}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2.5,
                                                borderRadius: 3,
                                                bgcolor: "white",
                                                border: "1px solid",
                                                borderColor: "grey.100",
                                                transition: "all 0.2s",
                                                "&:hover": { borderColor: "primary.light", bgcolor: "primary.lighter" },
                                            }}
                                        >
                                            <Icon icon={item.icon} width={28} color="#1a1a2e" />
                                            <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 700, mt: 1, mb: 0.5 }}>
                                                {item.title}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {item.desc}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* ===== CTA BANNER ===== */}
            <Box
                sx={{
                    position: "relative",
                    py: { xs: 6, md: 8 },
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundImage: `url(${HERO_IMAGE})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: "linear-gradient(135deg, rgba(26,26,46,0.92) 0%, rgba(15,21,37,0.9) 100%)",
                        },
                    }}
                />
                <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
                    <Stack alignItems="center" spacing={3}>
                        <Typography
                            variant="h3"
                            component="h2"
                            sx={{
                                fontWeight: 800,
                                color: "white",
                                textAlign: "center",
                            }}
                        >
                            {t("homePage.ctaTitle")}
                        </Typography>
                        <Typography
                            variant="h6"
                            component="p"
                            sx={{
                                color: "rgba(255,255,255,0.8)",
                                textAlign: "center",
                                fontWeight: 400,
                                maxWidth: 500,
                            }}
                        >
                            {t("homePage.ctaSubtitle")}
                        </Typography>
                        <Box
                            component="button"
                            type="button"
                            aria-label={t("homePage.ctaButton")}
                            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                            sx={{
                                px: 5,
                                py: 1.8,
                                fontSize: "1.1rem",
                                fontWeight: 700,
                                borderRadius: 3,
                                border: "none",
                                cursor: "pointer",
                                bgcolor: "white",
                                color: "primary.dark",
                                boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
                                transition: "all 0.2s",
                                "&:hover": {
                                    transform: "translateY(-2px)",
                                    boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
                                },
                            }}
                        >
                            {t("homePage.ctaButton")}
                        </Box>
                    </Stack>
                </Container>
            </Box>

            {/* ===== FOOTER ===== */}
            <Footer />
        </Box>
    );
}
